<script setup lang="ts">
import { RouterView, useRouter } from 'vue-router'
import { cmsTextReady, useCmsText } from "@/composables/useCmsText";
import { useHead } from '@unhead/vue'
import { onMounted, ref, watch } from "vue";
import AppLoader from "@/components/AppLoader.vue";
import { useLoaderStore } from '@/stores/loader';
import * as Fathom from 'fathom-client';
import { useAppStateStore } from '@/stores/appState';

// Sets Chart.defaults and registers Chart.js at module scope. Loaded here only
// so the loader waits for the chart.js chunk — the chart components import it
// directly, so the defaults are always in place before a chart reads them.
//
// Resolving on canvasFontsReady rather than on the import: importing the module
// only *starts* the font fetch, so gating on the import alone reveals the
// charts in fallback faces and lets them visibly re-typeset once the real ones
// land. canvasFontsReady uses allSettled internally, so a font that 404s
// cannot hold the loader open.
const chartDefaults = () =>
  import('@/data/chartDefaults').then((m) => m.canvasFontsReady)
const TeamsView = () => import('@/views/TeamsView.vue')
const HiresView = () => import('@/views/HiresView.vue')
const LeadersView = () => import('@/views/LeadersView.vue')
const IntroView = () => import('@/views/IntroView.vue')
const AppView = () => import('@/views/AppView.vue')

const loaderStore = useLoaderStore()

// lazy load the views, wait for them to import
const lazyLoadLoaded = ref(false)

loaderStore.addDependency(lazyLoadLoaded)

// only load the big content after the loader has started animated
onMounted(() => {
  setTimeout(() => {
    Promise.all(
      [
        TeamsView(),
        HiresView(),
        LeadersView(),
        IntroView(),
        AppView(),
        chartDefaults(),
      ]
    ).then(() => {
      lazyLoadLoaded.value = true
    })
  }, 100)
})

const headControl = useHead({
  title: '',
  meta: [
    {
      name: 'description',
      content: ''
    }
  ],
})

watch(() => cmsTextReady(), () => {
  headControl.patch({
    title: useCmsText('meta-title'),
    meta: [
      {
        name: 'description',
        content: useCmsText('meta-description')
      }
    ],
  })
})

const fathomIdElement = document.getElementById('FATHOM_ID')
const fathomId = fathomIdElement ? JSON.parse(fathomIdElement.innerText) : null
if (fathomId) {
  Fathom.load(fathomId, { auto: false, spa: 'hash' })
}

const router = useRouter()

const appStateStore = useAppStateStore()

const prevPathAndHash = ref()

watch(() => [router.currentRoute.value.path, router.currentRoute.value.hash], () => {
  // close the slideout if the route changes and the route is not /teams
  if (router.currentRoute.value.path !== '/teams') {
    appStateStore.breakdownSlideoutController.close()
  }

  const pathHash = router.currentRoute.value.path + router.currentRoute.value.hash

  // This watcher sometimes runs when it's the same values, so we need to check
  if (prevPathAndHash.value === pathHash) {
    return
  }

  prevPathAndHash.value = pathHash
  if (router.currentRoute.value.path !== '/') {
    appStateStore.trackPageView()
  }
}, { immediate: true })
</script>

<template>
  <RouterView />
  <AppLoader v-if="!loaderStore.hasLoadedAndAnimated" />
  <div id="slideout" />
</template>

